import { useLocation, useNavigate } from "react-router-dom"
import { buildMeasurementPath, formatDate, formatOrdinal, getSampleIdFromSearch, PATH_INFO } from "../utils/helper"
import { SIZE_LIST } from "../utils/helper"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import fileImg from '../../assets/images/icons/file-upload.svg'
import downArrowImg from '../../assets/images/icons/downArrow.svg'
import { Modal } from "../../components/Modal"
import errorImg from '../../assets/images/icons/errorOutline.svg'
import InfiniteScroll from "react-infinite-scroll-component"
import ReactLoading from "react-loading"
import { clientFetch } from "../../utils/clientFetch"
import { SamplesContext } from "../../context/samplesContext"

type SpecType = {
  created_at: string
  part_number: string
  sample_number: string
  kind: string
  repeat: number
  size: string
  color_no: string
  time: number
  id: string
}

type SpecMeta = {
  total: number
  page: number
  limit: number
  hasMore: boolean
  nextPage: number | null
}

type SpecApiResponse = {
  data: SpecType[]
  meta: SpecMeta
}

type ManualSpecItemInput = {
  id: string
  title: string
  value: string
}

type ManualSpecForm = {
  brand: string
  partNumber: string
  sampleNumber: string
  repeat: string
  itemName: string
  manufacturer: string
  patternNumber: string
  kind: '' | 'final' | 'sample'
  designedAt: string
  time: string
  colorNo: string
  size: string
  items: ManualSpecItemInput[]
}

type ManualSpecPayload = {
  brand: string
  part_number?: string | null
  sample_number?: string | null
  repeat: number
  item_name: string
  manufacturer?: string | null
  pattern_number?: string | null
  kind: 'final' | 'sample'
  designed_at: string
  time?: number | null
  color_no?: string | null
  size: string
  items: { title: string, value: number }[]
  onDuplicate?: 'overwrite' | 'cancel'
}

type BuildPayloadResult =
  | { ok: true, payload: ManualSpecPayload }
  | { ok: false, error: string }

type ApiErrorBody = {
  message?: string
  code?: string
  error_code?: string
  rowNumber?: number
  // valueは型が揺れるためunknownで受け、表示時に型チェックする
  value?: unknown
  duplicates?: {
    row: number
    part: string | null
    sample: string | null
    repeat: number
    size: string
    kind: string
  }[]
}

const SPEC_PAGE_LIMIT = 50
const DEFAULT_SPEC_META: SpecMeta = {
  total: 0,
  page: 1,
  limit: SPEC_PAGE_LIMIT,
  hasMore: false,
  nextPage: null
}
const SPEC_TABLE_SCROLL_ID = "spec-import-table-scroll"
const MANUAL_SPEC_ITEM_ID_START = 0
const MANUAL_SPEC_ITEM_ID_STEP = 1
const MANUAL_REPEAT_MIN = 0
const MANUAL_ITEM_VALUE_MIN = 0
const MANUAL_IDENTIFIER_PATTERN = /^[A-Za-z0-9_-]*$/
const MANUAL_IDENTIFIER_SANITIZE_PATTERN = /[^A-Za-z0-9_-]/g
const MANUAL_SAMPLE_STATUS_OPTIONS = [
  { value: 'final', label: '量産' },
  { value: 'sample', label: 'サンプル' }
]
const MANUAL_TIME_OPTIONS = [1, 2, 3, 4, 5]
const ERROR_VALUE_EMPTY_MESSAGE = '値が空です。'
const ERROR_VALUE_PREFIX = '問題の値:'
const ERROR_VALUE_ITEM_LABEL = '採寸項目'
const ERROR_VALUE_VALUE_LABEL = '値'
const ERROR_CODE_MESSAGE_MAP: Record<string, string> = {
  DUPLICATE: '重複データがあります。',
  TIME_REQUIRED: 'サンプルの場合、サンプル状況を選択してください。',
  SIZE_REQUIRED: 'サイズは必須です。',
  REPEAT_REQUIRED: 'リピートは必須です。',
  ITEM_REQUIRED: 'アイテム名は必須です。',
  ITEMS_REQUIRED: '採寸項目は必須です。',
  ITEM_TITLE_REQUIRED: '採寸項目名は必須です。',
  ITEM_TITLE_DUPLICATE: '採寸項目名が重複しています。',
  ITEM_VALUE_INVALID: '採寸項目の値が不正です。',
  BRAND_REQUIRED: 'ブランド名 or 顧客名 は必須です。',
  REPEAT_INVALID: 'リピートは数値で入力してください。',
  PART_OR_SAMPLE_REQUIRED: '品番またはサンプルNoのどちらかは必須です。',
  KIND_REQUIRED: '量産 or サンプル は必須です。',
  KIND_INVALID: '量産 or サンプル に無効な値が入力されています。',
  INVALID_DESIGNED_AT: '日付 or 作成日 が不正です。日付形式を確認してください。',
  HEADER_MISSING: 'CSVヘッダーに必須カラムが不足しています。',
  HEADER_INVALID: 'CSVヘッダーに不正なカラム名が含まれています。'
}

type ImportErrorDetail = {
  message: string
  rowNumber?: number
  // valueは型が揺れるためunknownで受け、表示時に型チェックする
  value?: unknown
  duplicates?: {
    row: number
    part: string | null
    sample: string | null
    repeat: number
    size: string
    kind: string
  }[]
}

const getErrorMessageFromApi = (errorBody: ApiErrorBody | null, fallback: string) => {
  const errorCode = errorBody?.code || errorBody?.error_code
  if (errorCode && ERROR_CODE_MESSAGE_MAP[errorCode]) {
    return ERROR_CODE_MESSAGE_MAP[errorCode]
  }
  return errorBody?.message || fallback
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

const isNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value)
}

const isMeasurementItemErrorValue = (value: unknown): value is { title?: unknown, rawValue?: unknown } => {
  return isRecord(value) && ('title' in value || 'rawValue' in value)
}

const isStringArray = (value: unknown): value is string[] => {
  return Array.isArray(value) && value.every(isString)
}

const sanitizeManualIdentifier = (value: string): string => {
  return value.replace(MANUAL_IDENTIFIER_SANITIZE_PATTERN, '')
}

const isManualIdentifier = (value: string): boolean => {
  return MANUAL_IDENTIFIER_PATTERN.test(value)
}

// unknownで受けた値はここで型チェックし、表示可能な文字列へ安全に変換する
const getErrorValueMessage = (value: unknown): string => {
  if (value === undefined || value === null) {
    return ''
  }
  if (isStringArray(value)) {
    const hasValue = value.some((item) => item !== '')
    return hasValue ? `${ERROR_VALUE_PREFIX} ${value.join(', ')}` : ERROR_VALUE_EMPTY_MESSAGE
  }
  if (isString(value)) {
    return value.trim() !== '' ? `${ERROR_VALUE_PREFIX} ${value}` : ERROR_VALUE_EMPTY_MESSAGE
  }
  if (isMeasurementItemErrorValue(value)) {
    const title = isString(value.title) ? value.title.trim() : ''
    const rawValue = isString(value.rawValue) ? value.rawValue.trim() : ''
    const parts = [
      title !== '' ? `${ERROR_VALUE_ITEM_LABEL}:${title}` : '',
      rawValue !== '' ? `${ERROR_VALUE_VALUE_LABEL}:${rawValue}` : ''
    ].filter((part) => part !== '')

    if (parts.length > 0) {
      return `${ERROR_VALUE_PREFIX} ${parts.join(' ')}`
    }

    return ERROR_VALUE_EMPTY_MESSAGE
  }
  try {
    const json = JSON.stringify(value)
    return json && json !== '{}' ? `${ERROR_VALUE_PREFIX} ${json}` : ERROR_VALUE_EMPTY_MESSAGE
  } catch (error) {
    return `${ERROR_VALUE_PREFIX} ${String(value)}`
  }
}

// APIエラーレスポンスを安全に読み取り、必要な情報だけ抽出する
const parseApiErrorBody = async (res: Response): Promise<ApiErrorBody | null> => {
  try {
    const data = await res.json()
    if (!isRecord(data)) {
      return null
    }
    const message = isString(data.message) ? data.message : undefined
    const code = isString(data.code) ? data.code : undefined
    const errorCode = isString(data.error_code) ? data.error_code : undefined
    const rowNumber = isNumber(data.rowNumber) ? data.rowNumber : undefined
    const details = isRecord(data.details) ? data.details : undefined
    const rawValue = data.value ?? details?.value
    const value = rawValue !== undefined ? rawValue : undefined
    const duplicates = Array.isArray(data.duplicates)
      ? data.duplicates
        .filter(isRecord)
        .map((item) => ({
          row: isNumber(item.row) ? item.row : 0,
          part: isString(item.part) ? item.part : null,
          sample: isString(item.sample) ? item.sample : null,
          repeat: isNumber(item.repeat) ? item.repeat : 0,
          size: isString(item.size) ? item.size : '',
          kind: isString(item.kind) ? item.kind : ''
        }))
        .filter((item) => item.row > 0)
      : undefined
    return {
      message,
      code,
      error_code: errorCode,
      rowNumber,
      value,
      duplicates
    }
  } catch (error) {
    return null
  }
}

export function Specification() {
  const navigate = useNavigate()
  const location = useLocation()
  const sampleIdFromSearch = getSampleIdFromSearch(location.search)
  const { sample, rowSample } = useContext(SamplesContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [specList, setSpecList] = useState<SpecType[]>([])
  const specMetaRef = useRef<SpecMeta>(DEFAULT_SPEC_META)
  const [specMeta, setSpecMeta] = useState<SpecMeta>(DEFAULT_SPEC_META)
  const [isSpecLoading, setIsSpecLoading] = useState(false)
  const [isSpecLoaded, setIsSpecLoaded] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [time, setTime] = useState('')
  const [isModal, setIsModal] = useState(false)
  const [isErrorModal, setIsErrorModal] = useState(false)
  const [isManualInputModalOpen, setIsManualInputModalOpen] = useState(false)
  // const [uploadError, setUploadError] = useState<{ row: number[], message: string }>()
  const [importErrorDetail, setImportErrorDetail] = useState<ImportErrorDetail | null>(null)
  const onCloseErrorModal = useCallback(() => {
    setIsErrorModal(false)
    setImportErrorDetail(null)
  }, [])
  const onCloseManualInputModal = useCallback(() => {
    setIsManualInputModalOpen(false)
  }, [])

  useEffect(() => {
    specMetaRef.current = specMeta
  }, [specMeta])

  useEffect(() => {
    setFile(null)
    setTime('')
  }, [])

  const fetchSpecifications = useCallback(async (options?: { append?: boolean }) => {
    const shouldAppend = options?.append ?? false
    const meta = specMetaRef.current
    const targetPage = shouldAppend ? (meta.nextPage ?? meta.page + 1) : 1
    if (shouldAppend && !meta.hasMore) {
      return
    }
    try {
      setIsSpecLoading(true)
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(SPEC_PAGE_LIMIT)
      })
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec?${params.toString()}`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: true
      })
      if (res.status === 401) {
        return
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      const data = await res.json() as SpecApiResponse
      const items: SpecType[] = Array.isArray(data?.data) ? data.data : []
      setSpecList(prev => {
        if (!shouldAppend) {
          return items
        }
        const map = new Map<string, SpecType>()
        prev.forEach(item => map.set(item.id, item))
        items.forEach((item: SpecType) => map.set(item.id, item))
        return Array.from(map.values())
      })
      setSpecMeta(data.meta ?? DEFAULT_SPEC_META)
    } catch (error) {
      console.log('ERROR FETCH')
    } finally {
      setIsSpecLoading(false)
      setIsSpecLoaded(true)
    }
  }, [navigate])

  const loadMoreSpecifications = useCallback(async () => {
    if (isSpecLoading || !specMetaRef.current.hasMore) {
      return
    }
    await fetchSpecifications({ append: true })
  }, [fetchSpecifications, isSpecLoading])

  useEffect(() => {
    fetchSpecifications()
  }, [fetchSpecifications])

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setFile(null)
    setTime('')
  }, [])

  const handleFileUpload = useCallback(async (onDuplicateMode?: 'skip' | 'overwrite') => {
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('time', time)
    if (onDuplicateMode) {
      formData.append('onDuplicate', onDuplicateMode)
    }

    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec/import/`, {
        method: "POST",
        body: formData,
        handleUnauthorized: true
      })

      if (res.status === 409) {
        const errorBody = await parseApiErrorBody(res)
        setImportErrorDetail({
          message: getErrorMessageFromApi(errorBody, 'エラーが発生しました。'),
          rowNumber: errorBody?.rowNumber,
          value: errorBody?.value,
          duplicates: errorBody?.duplicates
        })
        setIsModal(true)
        return
      }

      if (!res.ok) {
        const errorBody = await parseApiErrorBody(res)
        setImportErrorDetail({
          message: getErrorMessageFromApi(errorBody, 'エラーが発生しました。'),
          rowNumber: errorBody?.rowNumber,
          value: errorBody?.value,
          duplicates: errorBody?.duplicates
        })
        setIsErrorModal(true)
        return
      }

      await fetchSpecifications()
      resetFileInput()
    } catch (error) {
      console.error('Error uploading file')
      resetFileInput()
    }
  }, [file, time, fetchSpecifications, resetFileInput])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = e.dataTransfer.files[0]
      setFile(dropped)
    }
  }, [])

  return (
    <div className="relative bg-gray-100 rounded-[10px] pt-[22px] pb-[30px] px-[25px] mt-[25px] mx-[40px] min-w-[575px]">
      <div className='flex items-center w-full'>
        <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
        <span className='text-[18px]'>
          仕様書インポート
        </span>
      </div>
      <div className="flex justify-center flex-col-reverse pt-[25px] desktop-md:flex-row desktop-md:space-y-0">
        <Tabele
          specList={specList}
          hasMore={specMeta.hasMore}
          onLoadMore={loadMoreSpecifications}
          isLoaded={isSpecLoaded}
        />
        <div className="flex flex-col min-w-0 max-w-full justify-between mb-2.5 desktop-md:max-w-[1016px] desktop-md:pl-[20px] desktop-md:mb-0">
          <div
            className="flex items-center justify-center w-full h-[372px] bg-[#F9FAFF] rounded-[23px] desktop-md:h-[630px]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="flex items-center justify-center w-[192px] h-[192px] bg-[#E1E2FF] rounded-[50%]">
              <img src={fileImg} alt="file" className="w-[115px]" />
            </div>
            <div className="relative flex flex-col items-center ml-[26px]">
              <span className="mb-[9px] text-[#545899] font-bold">仕様書をドロップ</span>
              <span className="mb-[12px] text-[#545899] font-bold">または</span>
              <div
                className='flex items-center w-[165px] h-[41px] border-[2px] border-[#545899] rounded-[8px] text-[#545899] font-bold bg-white'
              >
                <input
                  type="file"
                  id='file-input'
                  accept='.csv'
                  ref={fileInputRef}
                  className='sr-only'
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFile(e.target.files[0])
                    }
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                />
                <label htmlFor='file-input' className='flex items-center justify-center cursor-pointer w-full h-full'>
                  <span>
                    ファイルを選択
                  </span>
                </label>
              </div>
              <span className="absolute -bottom-[30px] flex max-w-[165px] min-w-0 items-center space-x-[5px] pt-[5px]">
                <p
                  className="flex-1 truncate"
                  title={file?.name || ''}
                  aria-label={file?.name || ''}
                >
                  {file?.name}
                </p>
                {file && <button onClick={() => setFile(null)}>✕</button>}
              </span>
            </div>
          </div>
          <div className="flex justify-center items-center w-full space-x-[14px] mt-[15px] desktop-md:mt-0">
            <button onClick={() => navigate(buildMeasurementPath(sampleIdFromSearch || rowSample.id || sample.id))} className="bg-black w-[158px] h-[37px] rounded-[50px] tablet-lg:w-[200px] tablet-lg:h-[47px]">
              <span className="text-white text-sm tablet-lg:text-base">検寸画面に戻る</span>
            </button>
            <button
              className="min-w-28 max-w-[143px] h-[37px] rounded-[50px] bg-[#C5C7FF] tablet-lg:w-[200px] tablet-lg:h-[47px]"
              onClick={() => setIsManualInputModalOpen(true)}
            >
              <span className="text-[#545899] font-bold text-sm tablet-lg:text-base">入力</span>
            </button>
            <button
              className="w-[143px] h-[37px] rounded-[50px] bg-[#C5C7FF] tablet-lg:w-[200px] tablet-lg:h-[47px]"
              onClick={() => handleFileUpload()}
            >
              <span className="text-[#545899] font-bold text-sm tablet-lg:text-base">インポート</span>
            </button>
            <label className="space-x-[5px] h-max">
              <span className="text-[#545899] font-bold">サンプル状況 :</span>
              <select
                className="h-[30px] bg-transparent text-center pr-[5px] text-[#474980] border-0 border-b-2 border-[#545899] focus:outline-none focus:ring-0 focus:border-[#3a3c69] peer"
                value={time || ''}
                onChange={(e) => { setTime(e.target.value) }}
              >
                <option value={''} hidden>--</option>
                {MANUAL_TIME_OPTIONS.map((value) => (
                  <option key={value} value={String(value)}>{formatOrdinal(value)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
      <DuplicatesModal isOpen={isModal} onClose={() => setIsModal(false)} detail={importErrorDetail} handleFileUpload={handleFileUpload} />
      <ErrorModal isOpen={isErrorModal} onClose={onCloseErrorModal} detail={importErrorDetail} />
      <ManualInputModal
        isOpen={isManualInputModalOpen}
        onClose={onCloseManualInputModal}
        onCreated={fetchSpecifications}
      />
    </div>
  )
}

function Tabele({ specList, hasMore, onLoadMore, isLoaded }: { specList: SpecType[], hasMore: boolean, onLoadMore: () => void, isLoaded: boolean }) {
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [sortedSpecList, setSortedSpecList] = useState<SpecType[]>([])
  const endMessage = isLoaded && !hasMore
    ? <div className="py-3 text-center text-xs text-[#666666]">表示可能な履歴はすべて読み込み済みです。</div>
    : (
      <div className="flex items-center justify-center py-3 text-center text-xs text-[#666666]">
        <ReactLoading type="spin" color="#666666" height={16} width={16} className="inline-block mr-2" />
        <span>仕様書履歴を読み込み中です…</span>
      </div>
    )

  useEffect(() => {
    if (Array.isArray(specList)) {
      const sorted = specList.slice().sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
      })
      setSortedSpecList(sorted)
    }
  }, [specList, sortOrder])

  return (
    <div
      className="desktop-md:bg-white desktop-md:rounded-[20px] desktop-md:pt-5 desktop-md:pb-[30px] desktop-md:px-4 desktop-md:shadow-[0px_4px_4px_0px_#00000040]"
    >
      <div className='flex items-center justify-between w-full pb-[10px] pl-[9px] pr-[14px]'>
        <span className='text-[16px] opacity-0 desktop-md:opacity-100'>
          仕様書インポート履歴
        </span>
        <div className="flex space-x-[8px]">
          <label className="flex items-center">
            <input
              type="radio"
              name="sort"
              className="w-[26px] h-[26px] mr-[8px] accent-[#545899]"
              checked={sortOrder === 'asc'}
              value="asc"
              onChange={() => setSortOrder('asc')}
            />
            撮影日順(昇順)
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="sort"
              className="w-[26px] h-[26px] mr-[8px] accent-[#545899]"
              checked={sortOrder === 'desc'}
              value="desc"
              onChange={() => setSortOrder('desc')}
            />
            撮影日順(降順)
          </label>
        </div>
      </div>
      <div className="h-[380px] overflow-y-scroll border border-gray-400 bg-white desktop-md:w-[655px] desktop-md:h-[660px]" id={SPEC_TABLE_SCROLL_ID}>
        <InfiniteScroll
          dataLength={sortedSpecList.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            <div className="flex items-center justify-center py-3 text-center text-xs text-[#666666]">
              <ReactLoading type="spin" color="#666666" height={16} width={16} className="inline-block mr-2" />
              <span>仕様書履歴を読み込み中です…</span>
            </div>}
          endMessage={endMessage}
          scrollableTarget={SPEC_TABLE_SCROLL_ID}
          scrollThreshold="200px"
          style={{ overflow: "visible" }}
        >
          <table className="w-full">
            <thead className="bg-[#3E3E3E] h-[23px]">
              <tr className="sticky inset-0 bg-[#3E3E3E] text-[12px] text-white">
                <th className="sticky top-0 w-[165px] font-normal">検寸日時</th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  品番
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  サンプルNo
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  サンプル状況
                </th>
                <th className="sticky top-0 w-[80px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  リピート
                </th>
                <th className="sticky top-0 w-[80px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  サイズ
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  カラー
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {sortedSpecList.map((el: SpecType) => (
                <tr key={el.id} className='text-[14px] h-[30px]'>
                  <td className="h-[30px] border border-l-0 border-t-0 border-gray-400 text-center text-[16px]">{formatDate(el.created_at)}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.part_number || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.sample_number || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.kind === 'final' ? 'FINAL' : formatOrdinal(el.time)}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.repeat > 0 ? el.repeat : 0}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.size || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.color_no || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  )
}

function ManualInputModal({ isOpen, onClose, onCreated }: { isOpen: boolean, onClose: () => void, onCreated: () => Promise<void> }) {
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState('')
  const [duplicateMessage, setDuplicateMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isSizeOpen, setIsSizeOpen] = useState(false)
  const sizeDropdownRef = useRef<HTMLDivElement | null>(null)
  const errorMessageRef = useRef<HTMLParagraphElement | null>(null)
  const [duplicatePayload, setDuplicatePayload] = useState<ManualSpecPayload | null>(null)
  const manualSpecItemIdCounterRef = useRef(MANUAL_SPEC_ITEM_ID_START)
  const createManualSpecItemId = useCallback(() => {
    manualSpecItemIdCounterRef.current += MANUAL_SPEC_ITEM_ID_STEP
    return `manual-item-${manualSpecItemIdCounterRef.current}`
  }, [])
  const createManualSpecItem = useCallback((): ManualSpecItemInput => ({
    id: createManualSpecItemId(),
    title: '',
    value: ''
  }), [createManualSpecItemId])
  const getTodayDateInputValue = useCallback(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }, [])
  const createManualSpecForm = useCallback((): ManualSpecForm => ({
    brand: '',
    partNumber: '',
    sampleNumber: '',
    repeat: '',
    itemName: '',
    manufacturer: '',
    patternNumber: '',
    kind: '',
    designedAt: getTodayDateInputValue(),
    time: '',
    colorNo: '',
    size: '',
    items: [createManualSpecItem()]
  }), [createManualSpecItem, getTodayDateInputValue])
  const [form, setForm] = useState<ManualSpecForm>(() => createManualSpecForm())

  useEffect(() => {
    if (isOpen) {
      manualSpecItemIdCounterRef.current = MANUAL_SPEC_ITEM_ID_START
      setForm(createManualSpecForm())
      setErrorMessage('')
      setDuplicateMessage('')
      setDuplicatePayload(null)
      setIsSaving(false)
      setIsSizeOpen(false)
    }
  }, [createManualSpecForm, isOpen])

  useEffect(() => {
    if (!isSizeOpen) return
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (sizeDropdownRef.current && target && !sizeDropdownRef.current.contains(target)) {
        setIsSizeOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isSizeOpen])

  useEffect(() => {
    if (!errorMessage) return
    errorMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [errorMessage])

  const updateForm = useCallback((patch: Partial<ManualSpecForm>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }, [])

  const updateItem = useCallback((index: number, patch: Partial<ManualSpecItemInput>) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, idx) => (idx === index ? { ...item, ...patch } : item))
    }))
  }, [])

  const addItem = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, createManualSpecItem()]
    }))
  }, [createManualSpecItem])

  const removeItem = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index)
    }))
  }, [])

  const trimText = useCallback((value: string) => value.trim(), [])

  const buildPayload = useCallback((): BuildPayloadResult => {
    const brand = trimText(form.brand)
    const partNumber = trimText(form.partNumber)
    const sampleNumber = trimText(form.sampleNumber)
    const itemName = trimText(form.itemName)
    const manufacturer = trimText(form.manufacturer)
    const patternNumber = trimText(form.patternNumber)
    const colorNo = trimText(form.colorNo)
    const size = trimText(form.size)
    const repeatValue = Number(form.repeat)

    if (!brand) return { ok: false, error: 'ブランド名 or 顧客名は必須です。' }
    if (!itemName) return { ok: false, error: 'アイテム名は必須です。' }
    if (!partNumber && !sampleNumber) return { ok: false, error: '品番またはサンプルNoのどちらかは必須です。' }
    if (partNumber && !isManualIdentifier(partNumber)) return { ok: false, error: '品番は半角英数字・ハイフン・アンダースコアのみ入力できます。' }
    if (sampleNumber && !isManualIdentifier(sampleNumber)) return { ok: false, error: 'サンプルNoは半角英数字・ハイフン・アンダースコアのみ入力できます。' }
    if (patternNumber && !isManualIdentifier(patternNumber)) return { ok: false, error: 'パターンNoは半角英数字・ハイフン・アンダースコアのみ入力できます。' }
    if (!form.repeat) return { ok: false, error: 'リピートは必須です。' }
    if (!Number.isFinite(repeatValue)) return { ok: false, error: 'リピートは数値で入力してください。' }
    if (repeatValue < MANUAL_REPEAT_MIN) return { ok: false, error: 'リピートは0以上で入力してください。' }
    if (!form.kind) return { ok: false, error: '量産 or サンプルは必須です。' }
    if (!form.designedAt) return { ok: false, error: '日付 or 作成日は必須です。' }
    if (form.kind === 'sample' && !form.time) return { ok: false, error: 'サンプル状況を選択してください。' }
    if (!size) return { ok: false, error: 'サイズは必須です。' }

    const normalizedItems = form.items
      .map((item) => ({ title: trimText(item.title), value: trimText(item.value) }))
      .filter((item) => item.title !== '' || item.value !== '')

    if (normalizedItems.length === 0) return { ok: false, error: '採寸項目は必須です。' }

    for (let i = 0; i < normalizedItems.length; i += 1) {
      const item = normalizedItems[i]
      if (!item.title) return { ok: false, error: '採寸項目名は必須です。' }
      if (item.value === '') return { ok: false, error: '採寸項目の値は必須です。' }
      if (!Number.isFinite(Number(item.value))) return { ok: false, error: '採寸項目の値が不正です。' }
      if (Number(item.value) < MANUAL_ITEM_VALUE_MIN) return { ok: false, error: '採寸項目の値は0以上で入力してください。' }
    }

    return {
      ok: true,
      payload: {
        brand,
        part_number: partNumber || null,
        sample_number: sampleNumber || null,
        repeat: repeatValue,
        item_name: itemName,
        manufacturer: manufacturer || null,
        pattern_number: patternNumber || null,
        kind: form.kind as 'final' | 'sample',
        designed_at: form.designedAt,
        time: form.kind === 'sample' ? Number(form.time) : null,
        color_no: colorNo || null,
        size,
        items: normalizedItems.map((item) => ({
          title: item.title,
          value: Number(item.value)
        }))
      }
    }
  }, [form, trimText])

  const submitManualSpec = useCallback(async (payload: ManualSpecPayload) => {
    setIsSaving(true)
    setErrorMessage('')
    setDuplicateMessage('')
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        handleUnauthorized: true
      })

      if (res.status === 401) {
        return
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (res.status === 409) {
        const errorBody = await parseApiErrorBody(res)
        setDuplicateMessage(getErrorMessageFromApi(errorBody, '重複データがあります。'))
        setDuplicatePayload(payload)
        return
      }

      if (!res.ok) {
        const errorBody = await parseApiErrorBody(res)
        setErrorMessage(getErrorMessageFromApi(errorBody, '登録に失敗しました。'))
        return
      }

      await onCreated()
      onClose()
    } catch (error) {
      setErrorMessage('登録に失敗しました。')
    } finally {
      setIsSaving(false)
    }
  }, [navigate, onClose, onCreated])

  const handleSubmit = useCallback(async () => {
    const result = buildPayload()
    if (!result.ok) {
      setErrorMessage(result.error)
      return
    }
    setDuplicatePayload(null)
    await submitManualSpec(result.payload)
  }, [buildPayload, submitManualSpec])

  const handleOverwrite = useCallback(async () => {
    if (!duplicatePayload) return
    await submitManualSpec({ ...duplicatePayload, onDuplicate: 'overwrite' })
  }, [duplicatePayload, submitManualSpec])

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="min-w-[450px] max-w-[1000px] min-h-[250px] self-start my-6 flex max-h-[calc(100vh-48px)] flex-col overflow-hidden p-[25px] desktop-md:my-0 desktop-md:self-auto desktop-md:max-h-[90vh]"
      >
        <div className="flex min-h-0 w-full flex-col">
          <div className="flex items-center mb-4">
            <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
            <span className="text-[18px] font-bold">仕様書登録</span>
          </div>
          <div className="min-h-0 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 items-start gap-4 desktop-md:grid-cols-[minmax(320px,1fr)_minmax(420px,1.35fr)]">
              <div className="rounded border border-[#D1D5DB] bg-[#FAFAFA] p-3">
                <div className="space-y-3">
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>日付 or 作成日<span className="text-red-600"> *</span></span>
                    <input
                      type="date"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.designedAt}
                      onChange={(e) => updateForm({ designedAt: e.target.value })}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>ブランド名 or 顧客名<span className="text-red-600"> *</span></span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.brand}
                      onChange={(e) => updateForm({ brand: e.target.value })}
                      placeholder="例: ブランド名"
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>アイテム名<span className="text-red-600"> *</span></span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.itemName}
                      onChange={(e) => updateForm({ itemName: e.target.value })}
                      placeholder="例: Tシャツ"
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>品番</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.partNumber}
                      onChange={(e) => updateForm({ partNumber: sanitizeManualIdentifier(e.target.value) })}
                      placeholder="例: PN-123456"
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>サンプルNo</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.sampleNumber}
                      onChange={(e) => updateForm({ sampleNumber: sanitizeManualIdentifier(e.target.value) })}
                      placeholder="例: SN-123456"
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>リピート<span className="text-red-600"> *</span></span>
                    <input
                      type="number"
                      min={MANUAL_REPEAT_MIN}
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.repeat}
                      onChange={(e) => {
                        const nextRepeat = e.target.value
                        if (nextRepeat === '') {
                          updateForm({ repeat: '' })
                          return
                        }
                        const parsedRepeat = Number(nextRepeat)
                        if (!Number.isFinite(parsedRepeat)) {
                          return
                        }
                        if (parsedRepeat < MANUAL_REPEAT_MIN) {
                          updateForm({ repeat: String(MANUAL_REPEAT_MIN) })
                          return
                        }
                        updateForm({ repeat: nextRepeat })
                      }}
                      placeholder="例: 1"
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>量産 or サンプル<span className="text-red-600"> *</span></span>
                    <select
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.kind}
                      onChange={(e) => updateForm({ kind: e.target.value as ManualSpecForm['kind'] })}
                    >
                      <option value="" hidden>--</option>
                      {MANUAL_SAMPLE_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>サンプル状況{form.kind === 'sample' && <span className="text-red-600"> *</span>}</span>
                    <select
                      className={`w-full rounded border border-[#D1D5DB] px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)] ${form.kind !== 'sample' ? 'bg-[#F3F4F6] cursor-not-allowed' : 'bg-white'}`}
                      value={form.time}
                      onChange={(e) => updateForm({ time: e.target.value })}
                      disabled={form.kind !== 'sample'}
                    >
                      <option value="" hidden>--</option>
                      {MANUAL_TIME_OPTIONS.map((value) => (
                        <option key={value} value={String(value)}>{formatOrdinal(value)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>サイズ<span className="text-red-600"> *</span></span>
                    <div className="relative" ref={sizeDropdownRef}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between rounded border border-[#D1D5DB] bg-white px-2 py-1 text-left shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                        onClick={() => setIsSizeOpen((prev) => !prev)}
                        onPointerDown={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        <span>{form.size || '--'}</span>
                        <img
                          src={downArrowImg}
                          alt=""
                          aria-hidden="true"
                          className="w-2.5 h-2.5"
                        />
                      </button>
                      {isSizeOpen && (
                        <div
                          className="absolute left-0 right-0 top-[38px] z-20 max-h-[220px] overflow-y-auto rounded border border-[#D1D5DB] bg-white p-2 shadow"
                          onPointerDown={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          {Object.entries(SIZE_LIST).map(([groupKey, groupData]) => (
                            <div key={groupKey} className="mb-2 last:mb-0">
                              <p className="text-xs font-bold text-[#666]">{groupData.label}</p>
                              <div className="mt-1 space-y-1">
                                {groupData.size.map((size) => (
                                  <button
                                    key={size}
                                    type="button"
                                    className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-[#F3F4F6]"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      updateForm({ size })
                                      setIsSizeOpen(false)
                                    }}
                                  >
                                    {size}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>カラー</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.colorNo}
                      onChange={(e) => updateForm({ colorNo: e.target.value })}
                      placeholder="例: White"
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>工場名</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.manufacturer}
                      onChange={(e) => updateForm({ manufacturer: e.target.value })}
                      placeholder="例: factory"
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>パターンNo</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.patternNumber}
                      onChange={(e) => updateForm({ patternNumber: sanitizeManualIdentifier(e.target.value) })}
                      placeholder="例: P-123456"
                    />
                  </label>
                </div>
              </div>
              <div className="rounded border border-[#D1D5DB] bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="inline-flex flex-wrap items-baseline gap-1 text-sm font-bold">
                    <span>採寸項目</span>
                    <span className="text-xs font-medium text-[#6B7280]">(例:着丈)</span>
                    <span className="text-red-600">*</span>
                  </span>
                  <button
                    type="button"
                    className="border px-3 py-1 rounded-[20px] bg-[#C5C7FF] text-[#545899] text-xs font-semibold"
                    onClick={addItem}
                  >
                    ＋ 追加
                  </button>
                </div>
                <div className="mt-2 max-h-[480px] overflow-y-auto rounded border border-[#D1D5DB]">
                  <table className="w-full table-fixed border-collapse">
                    <thead className="sticky top-0 z-10 bg-[#F6F7FB]">
                      <tr className="text-left text-xs bg-[#3E3E3E] text-white">
                        <th className="border-b border-r border-[#D1D5DB] px-2 py-2 font-medium">採寸項目名</th>
                        <th className="w-[120px] border-b border-r border-[#D1D5DB] px-2 py-2 font-medium">採寸値</th>
                        <th className="w-[80px] border-b border-[#D1D5DB] px-2 py-2 font-medium text-center">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, index) => (
                        <tr key={item.id} className="text-sm">
                          <td className="border-b border-r border-[#D1D5DB] p-2 align-top">
                            {/* 支援技術向けに、sr-onlyでlabel付与 */}
                            <label htmlFor={`measure-item-title-${item.id}`} className="sr-only">
                              採寸項目名
                            </label>
                            <input
                              id={`measure-item-title-${item.id}`}
                              type="text"
                              className="w-full rounded border border-[#D1D5DB] px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                              placeholder="採寸項目を入力"
                              value={item.title}
                              onChange={(e) => updateItem(index, { title: e.target.value })}
                            />
                          </td>
                          <td className="border-b border-r border-[#D1D5DB] p-2 align-top">
                            {/* 支援技術向けに、sr-only で label を付与 */}
                            <label htmlFor={`measure-item-value-${item.id}`} className="sr-only">
                              採寸値
                            </label>
                            <input
                              id={`measure-item-value-${item.id}`}
                              type="number"
                              min={MANUAL_ITEM_VALUE_MIN}
                              step="0.1"
                              className="w-full rounded border border-[#D1D5DB] px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                              placeholder="例: 70.5"
                              value={item.value}
                              onChange={(e) => {
                                const nextValue = e.target.value
                                if (nextValue === '') {
                                  updateItem(index, { value: '' })
                                  return
                                }
                                const parsedValue = Number(nextValue)
                                if (!Number.isFinite(parsedValue)) {
                                  return
                                }
                                if (parsedValue < MANUAL_ITEM_VALUE_MIN) {
                                  updateItem(index, { value: String(MANUAL_ITEM_VALUE_MIN) })
                                  return
                                }
                                updateItem(index, { value: nextValue })
                              }}
                            />
                          </td>
                          <td className="border-b border-[#D1D5DB] p-2 text-center align-top">
                            {form.items.length > 1 && (
                              <button
                                type="button"
                                className="border px-2 py-0.5 rounded-[20px] text-xs bg-[#FBD1D8] border-[#F7B8C4] text-[#b85f5f] font-semibold shadow-[0_1px_2px_rgba(155,44,44,0.35)]"
                                onClick={() => removeItem(index)}
                              >
                                削除
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex shrink-0 items-center justify-end space-x-3">
            {errorMessage && (
              <p ref={errorMessageRef} className="mr-auto text-sm text-red-600">
                {errorMessage}
              </p>
            )}
            <button
              className="border px-4 py-2 rounded-[25px] text-[#666] font-bold"
              onClick={onClose}
              disabled={isSaving}
            >
              閉じる
            </button>
            <button
              className="border px-4 py-2 rounded-[25px] bg-[#C5C7FF] text-[#545899] font-bold"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              登録
            </button>
          </div>
        </div>
      </Modal>
      <ManualDuplicateConfirmModal
        isOpen={duplicateMessage !== ''}
        message={duplicateMessage}
        isSaving={isSaving}
        onCancel={() => {
          setDuplicateMessage('')
          setDuplicatePayload(null)
        }}
        onOverwrite={handleOverwrite}
      />
    </>
  )
}

function DuplicatesModal({ isOpen, onClose, detail, handleFileUpload }: { isOpen: boolean, onClose: () => void, detail: ImportErrorDetail | null, handleFileUpload: (action: 'skip' | 'overwrite') => void }) {
  const duplicateRows = detail?.duplicates?.map((item) => item.row).filter((row) => typeof row === 'number') ?? []
  const uniqueDuplicateRows = Array.from(new Set(duplicateRows))
  const hasDuplicateRows = uniqueDuplicateRows.length > 0
  const fallbackRowNumber = detail?.rowNumber
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="min-w-[450px] max-w-[1000px] min-h-[250px] max-h-fit flex flex-col items-center justify-center p-[25px]"
    >
      <span className="text-[16px] text-center">
        <span>
          CSVの
          {hasDuplicateRows && (
            <span>{uniqueDuplicateRows.map((row) => `${row}行目`).join('、')}</span>
          )}
          {!hasDuplicateRows && fallbackRowNumber && (
            <span>{fallbackRowNumber}行目</span>
          )}
          {!hasDuplicateRows && !fallbackRowNumber && '行目'}
          と既存データが重複しています。
        </span>
        <p>上書きしますか？</p>
      </span>
      <div className="space-x-[25px] mt-[35px]">
        <button
          className="border p-2 rounded-[25px] w-[150px] border-[#666]"
          onClick={() => {
            handleFileUpload('skip')
            onClose()
          }}
        >
          <p className="text-[#666] font-bold">いいえ</p>
        </button>
        <button
          className="border p-2 rounded-[25px] w-[150px] bg-[#C5C7FF]"
          onClick={() => {
            handleFileUpload('overwrite')
            onClose()
          }}>
          <p className="text-[#545899] font-bold">はい</p>
        </button>
      </div>
    </Modal>
  )
}

function ManualDuplicateConfirmModal({
  isOpen,
  message,
  isSaving,
  onCancel,
  onOverwrite
}: {
  isOpen: boolean
  message: string
  isSaving: boolean
  onCancel: () => void
  onOverwrite: () => void
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      className="min-w-[300px] max-w-[300px] min-h-[150px] flex flex-col items-center justify-center p-[25px]"
    >
      <div className="w-full text-center">
        <p className="text-[16px]">{message || '重複データがあります。'}</p>
        <p className="mt-2 text-[14px]">上書きして保存しますか？</p>
        <div className="mt-[25px] flex items-center justify-center space-x-3">
          <button
            className="border px-4 py-2 rounded-[25px] w-[100px] text-[#666] font-bold"
            onClick={onCancel}
            disabled={isSaving}
          >
            いいえ
          </button>
          <button
            className="border px-4 py-2 rounded-[25px] w-[100px] bg-[#C5C7FF] text-[#545899] font-bold"
            onClick={onOverwrite}
            disabled={isSaving}
          >
            はい
          </button>
        </div>
      </div>
    </Modal>
  )
}

function ErrorModal({ isOpen, onClose, detail }: { isOpen: boolean, onClose: () => void, detail: ImportErrorDetail | null }) {
  const errorValueMessage = getErrorValueMessage(detail?.value)
  const hasDetailValue = detail?.value !== undefined && detail?.value !== null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="min-w-[480px] max-w-fit max-h-[460px] flex flex-col items-center justify-center p-6"
    >
      <div className="w-full">
        <div className="flex items-center justify-center">
          <img src={errorImg} alt="error" className="w-6 mr-2" />
          <p className="text-center font-bold text-[16px]">
            {detail?.message || 'エラーが発生しました。'}
          </p>
        </div>
        {hasDetailValue && (
          <p className="mt-3 text-center text-[14px] text-[#555] break-all">
            {errorValueMessage}
          </p>
        )}
        <p className="mt-4 text-center text-[12px]">
          CSV
          {detail?.rowNumber && (
            <span>
              の
              <span className="font-bold text-sm">
                {detail?.rowNumber}行目
              </span>
            </span>
          )}
          を修正して再度アップロードしてください。
        </p>
      </div>
      <button
        onClick={onClose}
        className="border p-2 rounded-[25px] w-[150px] mt-[25px] border-[#666]"
      >
        <p className="text-[#666] font-bold">戻る</p>
      </button>
    </Modal>
  )
}
